import React, { useRef } from 'react';
import { Button } from 'carbon-components-react';
import { Upload16 } from '@carbon/icons-react';

interface FileUploadDropZoneProps {
  acceptedFormats: string;
  maxSize: string;
  /** Lowercase file extensions to allow, e.g. ['.kml', '.kmz', '.zip', '.geojson', '.tab'] */
  allowedExtensions: string[];
  /** Maximum file size in bytes */
  maxSizeBytes: number;
  onFileSelected: (file: File) => void;
  onValidationError?: (message: string) => void;
}

const FileUploadDropZone: React.FC<FileUploadDropZoneProps> = ({
  acceptedFormats,
  maxSize,
  allowedExtensions,
  maxSizeBytes,
  onFileSelected,
  onValidationError
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  function validateAndSelect(file: File) {
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (!allowedExtensions.includes(ext)) {
      onValidationError?.(
        `Unsupported file type. Accepted formats: ${acceptedFormats}.`
      );
      return;
    }
    if (file.size > maxSizeBytes) {
      onValidationError?.(
        `File is too large. Maximum allowed size is ${maxSize}.`
      );
      return;
    }
    onFileSelected(file);
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSelect(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleTargetKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openFilePicker();
    }
  };

  const acceptAttr = allowedExtensions.join(',');

  return (
    <div className="file-upload-drop-zone">
      <div
        className="file-upload-drop-zone__target"
        role="button"
        tabIndex={0}
        aria-label="Drag and drop files here or click to upload"
        onClick={openFilePicker}
        onKeyDown={handleTargetKeyDown}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <div className="file-upload-drop-zone__icon-wrapper">
          <Upload16 />
        </div>
        <div className="file-upload-drop-zone__heading">
          Drag and drop files here or click to upload
        </div>
        <div className="file-upload-drop-zone__sub-text">
          Supported formats: {acceptedFormats} - Max {maxSize}
        </div>
      </div>
      <Button
        kind="secondary"
        className="file-upload-drop-zone__button"
        onClick={openFilePicker}
      >
        Upload file
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptAttr}
        className="file-upload-drop-zone__input"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default FileUploadDropZone;
